import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { AppTextField } from "../../../components/form";
import { AppFormField, AppFormGrid } from "../../../components/layout";
import { GRID_HALF } from "../../../lib/ui/formLayout";
import {
  MB,
  QUOTA_FIELDS,
  type QuotaGroup,
  type QuotaKey,
  type QuotaValues,
} from "../../../services/customerPlanService";

/** Quota form values as text inputs (MB for byte fields); "" = no value. */
export type QuotaForm = Record<QuotaKey, string>;

const GROUPS: QuotaGroup[] = ["llm", "store", "count"];

function isMb(key: QuotaKey) {
  return QUOTA_FIELDS.find((f) => f.key === key)?.unit === "mb";
}

export function quotaToForm(values: QuotaValues | null | undefined): QuotaForm {
  const form = {} as QuotaForm;
  for (const { key } of QUOTA_FIELDS) {
    const v = values?.[key];
    if (v == null) {
      form[key] = "";
    } else if (isMb(key)) {
      const mb = v / MB;
      form[key] = Number.isInteger(mb) ? String(mb) : mb.toFixed(2).replace(/\.?0+$/, "");
    } else {
      form[key] = String(v);
    }
  }
  return form;
}

export function formToQuota(form: QuotaForm): QuotaValues {
  const values: QuotaValues = {};
  for (const { key } of QUOTA_FIELDS) {
    const raw = (form[key] ?? "").trim();
    if (!raw) {
      values[key] = null;
      continue;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) {
      values[key] = null;
    } else {
      values[key] = isMb(key) ? Math.round(n * MB) : Math.floor(n);
    }
  }
  return values;
}

export function formatQuota(key: QuotaKey, value: number | null | undefined, noCap: string): string {
  if (value == null) return noCap;
  if (isMb(key)) {
    const mb = value / MB;
    return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1).replace(/\.0$/, "")} MB`;
  }
  return value.toLocaleString();
}

/** Quota inputs grouped by the BEAS resource that lifts them. */
export function QuotaFieldsEditor({
  form,
  onChange,
  emptyHint,
}: {
  form: QuotaForm;
  onChange: (key: QuotaKey, value: string) => void;
  /** Helper text under empty fields (e.g. "No cap" or "Same as Free"). */
  emptyHint: string;
}) {
  const { t } = useTranslation();
  return (
    <Stack spacing={2.5}>
      {GROUPS.map((group) => (
        <Box key={group}>
          <Typography variant="subtitle2" fontWeight={650}>
            {t(`superAdmin.plans.group.${group}`)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t(`superAdmin.plans.groupHint.${group}`)}
          </Typography>
          <AppFormGrid spacing="compact">
            {QUOTA_FIELDS.filter((f) => f.group === group).map(({ key, unit }) => (
              <AppFormField key={key} size={GRID_HALF}>
                <AppTextField
                  label={`${t(`superAdmin.plans.field.${key}`)}${unit === "mb" ? " (MB)" : ""}`}
                  type="number"
                  value={form[key]}
                  onChange={(e) => onChange(key, e.target.value)}
                  helperText={form[key] === "" ? emptyHint : undefined}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
              </AppFormField>
            ))}
          </AppFormGrid>
        </Box>
      ))}
    </Stack>
  );
}

/** Read-only quota list grouped like the pricing page. */
export function QuotaSummary({
  values,
  noCapLabel,
  dense,
}: {
  values: QuotaValues | null | undefined;
  noCapLabel: string;
  dense?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Stack spacing={dense ? 1 : 1.5}>
      {GROUPS.map((group) => (
        <Box key={group}>
          <Typography variant="caption" color="text.secondary" fontWeight={650} sx={{ textTransform: "uppercase" }}>
            {t(`superAdmin.plans.group.${group}`)}
          </Typography>
          <Box
            component="dl"
            sx={{ m: 0, display: "grid", gridTemplateColumns: "1fr auto", columnGap: 2, rowGap: 0.25 }}
          >
            {QUOTA_FIELDS.filter((f) => f.group === group).map(({ key }) => (
              <Box key={key} sx={{ display: "contents" }}>
                <Typography component="dt" variant="body2" color="text.secondary">
                  {t(`superAdmin.plans.field.${key}`)}
                </Typography>
                <Typography component="dd" variant="body2" fontWeight={600} sx={{ m: 0, textAlign: "right" }}>
                  {formatQuota(key, values?.[key], noCapLabel)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Stack>
  );
}
